import { yupResolver } from "@hookform/resolvers/yup";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { CheckCircle, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { stripePromise } from "../../constants/constants";
import dummyImage from "../../images/background/dummyProfilebg.png";
import MapIcon from "../../images/svgs/MapIcon";
import { createBookAppointmentServices } from "../../services/apppointmentServices";
import { bookSlot } from "../../services/sheduleAppointmentsServices";
import { api1 } from "../../utils/axiosInstance";
import { convertToISO } from "../../utils/convertToISO";
import MentoringInputForm from "../mentoringInputForm/MentoringInputForm";
import Modal from "../modal/Modal";
import TermsAndConditionsComponent from "../termsAndConditionsComponent/termsAndConditionsComponent";
import { DateTime } from "luxon";

const schema = yup.object().shape({
  terms: yup.boolean().oneOf([true], "You must accept Terms and Conditions"),
});

const CheckoutForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentError, setPaymentError] = useState(null);
  // const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const auth = useSelector((state) => state.auth);

  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility
  const [isJitsiModal, setIsJitsiModal] = useState(false);
  const [jitsiDetails, setJitsiDetails] = useState();
  const [bookingId, setBookingId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMenteeApplicationOpen, setIsMenteeApplicationOpen] = useState(true);

  const navigate = useNavigate(); // Hook for navigation
  const location = useLocation();

  const userId = useSelector((state) => state.auth.id);

  const { formData, userData, transformedData } = location.state;
  const meetingTopic = `Meeting with ${userData.firstName || "Mentor"}`;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
  if (!stripe || !elements) {
    console.error("Stripe.js has not loaded yet.");
    return;
  }

  const cardElement = elements.getElement(CardElement);

  try {
    const response = await fetch(
      `${process.env.REACT_APP_NODE_APP_URL}api/stripe/create-payment-intent`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
            amount: Number(formData?.consultingFee * 100),
          }), // Total amount in dollars
      }
    );

    if (!response.ok) throw new Error(`Payment failed: ${response.status}`);

    const { clientSecret } = await response.json();

    const { error, paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: `${auth.firstName} ${auth.lastName}`,
          },
        },
      }
    );

    if (error) {
      let errorMessage = "Please complete your card details.";
      
      // Specific error messages for common cases
      if (error.code === "card_declined") {
        errorMessage = "Your card was declined. Try another payment method.";
      } else if (error.code === "insufficient_funds") {
        errorMessage = "Insufficient funds. Try another card or payment method.";
      } else if (error.code === "expired_card") {
        errorMessage = "Your card has expired. Update your card details.";
      }

      setPaymentError(errorMessage); 
        setTimeout(() => {
          setPaymentError(null);
        }, 2000);
      return;
    }

    const bookingResponse = await createBookAppointmentServices({
      mentorId: userData.userId,
      bookedAppointment: {
              comments: formData.comments,
              date: formData.date,
              selectedTime: formData.selectedTime,
              meetingForTime: formData.time.split("min")[0],
              status: 0,
      },
      payment: {
        paymentIntentId: paymentIntent.id,
        amount: Number(formData?.consultingFee),
        currency: paymentIntent.currency,
        status: paymentIntent.status,
        created: paymentIntent.created,
      },
    });
    setBookingId(bookingResponse.appointment._id);
    await bookSlot(transformedData);    
    setIsModalOpen(true); // Show the success modal}
  } catch (err) {
    setPaymentError(err.message || "Payment failed. Please try again.");
  }
};

  const handleModalClose = () => {
    setIsJitsiModal(true);
  };

  const [jitsiStatus, setJitsiStatus] = useState({
    isAuthenticated: false,
    isLoading: false,
    error: null,
  });
  const [jitsiMeeting, setJitsiMeeting] = useState({
    topic: null,
    date: null,
    consultingFee: null,
    startTime: null,
    duration: null,
    isCreating: false,
    link: null,
    error: null,
  });

  // Function to create a Jitsi meeting
  const createJitsiMeeting = async (booking) => {
    // Set the selected booking and open modal
    setSelectedBooking(booking);

    try {
      // Format the meeting topic using booking information
      const meetingTopic = `Meeting with ${userData.firstName || "Mentor"}`;
      const [startHour, startMinute] = formData.selectedTime
        .split(" - ")[0]
        .split(":");

      const localDate = DateTime.fromJSDate(formData.selectedDate).set({
        hour: Number(startHour),
        minute: Number(startMinute),
        second: 0,
      });

      // Convert to UTC
      const startTime = localDate.toUTC().toISO({ suppressMilliseconds: true });

      const response = await api1.post(
        "/api/jitsi/create-jitsi-meeting",
        {
          menteeId: userId,
          mentorId: userData.userId,
          topic: meetingTopic,
          start_time: startTime,
          duration: parseInt(formData.time.match(/\d+/)[0], 10),
        },
        {
          withCredentials: true,
        }
      );
      if (response.data?.meetingUrl) {
        setJitsiDetails(response.data);
        setJitsiMeeting({
          topic: meetingTopic,
          isCreating: false,
          startTime: startTime,
          date: formData.date, // Fix potential typo from `formData.data`
          link: response.data.meetingUrl,
          error: null,
        });
      } else {
        setJitsiMeeting({
          isCreating: false,
          link: null,
          error: "Failed to create meeting. No join URL returned.",
        });
      }
    } catch (error) {
      console.error("Error creating meeting:", error);

      // Check if the error is due to authentication
      if (error.response?.status === 401) {
        setJitsiStatus({
          isAuthenticated: false,
          isLoading: false,
          error: "Jitsi authentication required",
        });
      } else {
        setJitsiMeeting({
          isCreating: false,
          link: null,
          error: error.response?.data?.error || "Error creating Jitsi meeting",
        });
      }
    }
  };

  const handleModalCloseJitsi = () => {
    setIsJitsiModal(false);
    navigate("/dashboard/mentors/appointments");
  };

  const handleSaveMeeting = async () => {
    if (!jitsiDetails || !bookingId) {
      console.error("Jitsi details or booking id not available");
      return;
    }

    const { date: isoDate, selectedTime } = formData;
    const [startStr] = selectedTime.split(" - "); // e.g. ["00:15", "00:30"]
    const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // 3. Parse the date in the local zone…
    const base = DateTime.fromISO(isoDate, { zone: localZone });

    // 4. Build two DateTimes (start + end) and shift them to UTC
    const startUTC = base
      .set({
        hour: parseInt(startStr.split(":")[0], 10),
        minute: parseInt(startStr.split(":")[1], 10),
      })
      .toUTC()
      .toISO(); // e.g. "2025-04-29T19:15:00.000Z"

    try {
      const meetingData = {
        bookingId: bookingId,
        topic: meetingTopic || "JITSI MEETING LINK",
        date: startUTC,
        startTime: startUTC,
        endTime: jitsiDetails.end,
        duration: parseInt(formData.time.match(/\d+/)[0], 10),
        consultingFee: formData.consultingFee,
        meetingId: jitsiDetails.token || "",
        room: jitsiDetails.room || "",
        menteeId: userId,
        mentorId: userData.userId,
        meetingLink: jitsiDetails.meetingUrl || "",
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      await api1.post("api/meetings/create-appointment", meetingData);
    } catch (error) {
      console.error("Error saving meeting:", error);
    } finally {
      setIsModalOpen(false);
    }
  };

  useEffect(() => {
    if (jitsiDetails) {
      handleSaveMeeting();
    }
  }, [jitsiDetails]); // Trigger when jitsiDetails changes

  return (
    <>
      <Modal isOpen={isMenteeApplicationOpen} width="w-[1000px]">
        <MentoringInputForm
          setIsMenteeApplicationOpen={setIsMenteeApplicationOpen}
        />
      </Modal>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2">
            {/* Stripe Card Element */}
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2">Payment Details</h3>
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#424770",
                      "::placeholder": {
                        color: "#aab7c4",
                      },
                    },
                    invalid: {
                      color: "#9e2146",
                    },
                  },
                }}
              />
            </div>

            {/* Terms & Conditions */}
            <div className="flex items-center mt-4">
              <input
                type="checkbox"
                {...register("terms")}
                className="mr-2 accent-custom-blue size-4"
              />
              <span>
                I have read and accepted{" "}
                <button
                  // href="/dashboard/terms-and-conditions"
                  // target="_blank"
                  // rel="noopener noreferrer"
                  className="text-custom-blue font-semibold hover:underline"
                  onClick={() => setIsOpen(true)}
                  type="button"
                >
                  Terms and Conditions
                </button>
              </span>

              <Modal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                width="w-[60%]"
                label={"Terms and Conditions"}
              >
                <TermsAndConditionsComponent
                  fee={formData?.consultingFee}
                  mentorName={`${userData?.firstName} ${userData?.lastName}`}
                />
              </Modal>
            </div>
            <p className="text-red-500 text-sm">{errors.terms?.message}</p>

            {/* Payment Error */}
            {paymentError && (
              <p className="text-red-500 text-sm">{paymentError}</p>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="mt-4 bg-custom-blue text-white py-2 rounded px-4"
              disabled={!stripe}
            >
              Confirm and Pay
            </button>
          </div>

          {/* Booking Summary */}
          <div className="border-l pl-6">
            <div className="flex flex-col sm:flex-row items-center gap-4 mb-4">
              <img
                src={`${userData?.profileImage || dummyImage}`}
                alt={`${userData?.firstName} ${userData?.lastName}`}
                className="h-12 w-12 rounded-lg object-cover border"
              />
              <div>
                <h4 className="font-medium">
                  {`${userData?.firstName} ${userData?.lastName}`}
                </h4>
                <div className="flex items-center gap-1 text-yellow-500 mt-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      fill={i < 4 ? "currentColor" : "gray"}
                      className={i < 4 ? "text-yellow-500" : "text-gray-100"}
                    />
                  ))}
                  <span className="text-gray-500 text-sm">({7})</span>
                </div>
                <p className="text-gray-500 text-sm flex items-center gap-2">
                  <MapIcon />
                  {userData?.state}, {userData?.country}
                </p>
              </div>
            </div>
            <div className="text-gray-700 text-sm flex flex-col gap-5">
              <div className="flex justify-between">
                <strong>Date</strong>{" "}
                <span>{new Date(formData?.date).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <strong>Time</strong> <span>{formData?.selectedTime}</span>
              </div>
              <div className="flex justify-between">
                <strong>Mentoring Fee</strong>
                <span>${formData?.consultingFee}</span>
              </div>
              <div className="flex justify-between font-bold text-lg mt-2 border-t pt-3">
                <span>Total</span>{" "}
                <span className="text-custom-blue">
                  ${Number(formData?.consultingFee)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <Modal
          isOpen={isJitsiModal}
          onClose={handleModalCloseJitsi}
          width="w-[400px] md:w-[600px]"
        >
          <div className="flex flex-col gap-4 p-5">
            <h2 className="text-xl font-semibold text-center mb-2">
              {userData
                ? `Meeting with ${userData.firstName || "Mentor"}`
                : "Jitsi Meeting"}
            </h2>

            {jitsiMeeting.isCreating ? (
              <div className="text-center py-4">
                <p>Creating your meeting...</p>
                <div className="loader mt-4 mx-auto w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : jitsiMeeting.error ? (
              <div className="text-center text-red-600">
                <p>{jitsiMeeting.error}</p>
                <button
                  onClick={() => createJitsiMeeting(selectedBooking)}
                  className="mt-4 bg-blue-600 text-white font-medium py-2 px-6 rounded-full transition-all duration-200 hover:bg-blue-700"
                >
                  Try Again
                </button>
              </div>
            ) : jitsiMeeting.link ? (
              <div className="text-center">
                <p className="mb-4 text-green-600 font-semibold text-lg">
                  Your Jitsi meeting has been successfully created!
                </p>

                <div className="mb-4 bg-gray-50 p-5 rounded-lg shadow-md text-center">
                  <p className="text-gray-700">
                    The meeting details have been sent to your registered email.
                    Please check your inbox for further information.
                  </p>
                </div>

                <p className="mt-4 text-gray-600 text-sm">
                  If you don't see the email in your inbox, check your spam
                  folder or refresh your email.
                </p>
              </div>
            ) : (
              <div className="text-center">
                <p>Ready to create your Jitsi meeting for this appointment?</p>
                <button
                  onClick={() => createJitsiMeeting(selectedBooking)}
                  className="mt-4 bg-blue-600 text-white font-medium py-2 px-6 rounded-full transition-all duration-200 hover:bg-blue-700"
                >
                  Create Meeting
                </button>
              </div>
            )}
          </div>
        </Modal>
        {/* Success Modal */}
        <Modal isOpen={isModalOpen} label="Payment Successful">
          <div className="flex flex-col items-center text-center p-6">
            <CheckCircle size={48} className="text-green-500 mb-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              Payment Successful
            </h2>
            <p className="text-gray-600 mt-2">
              Your payment has been processed successfully. Thank you!
            </p>
            <button
              onClick={handleModalClose}
              className="mt-6 bg-custom-blue hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-all duration-200"
            >
              OK
            </button>
          </div>
        </Modal>
      </form>
    </>
  );
};

export default function BookAppointmentCheckoutForm() {
  return (
    <div className="flex justify-center items-center bg-white rounded-lg">
      <div className="w-full bg-white shadow-lg rounded-2xl p-6">
        <h2 className="text-xl font-semibold mb-4">Check out</h2>
        <Elements stripe={stripePromise}>
          <CheckoutForm />
        </Elements>
      </div>
    </div>
  );
}
